import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Lock, Sparkles, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'subscriber_email';

/**
 * Wrap any subscriber-gated page in this component.
 * Shows a subscribe form until the user is verified as an active newsletter subscriber.
 */
const SubscriberGate = ({ children }) => {
  const [status, setStatus] = useState('checking'); // checking | unlocked | locked
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const verify = useCallback(async (candidateEmail) => {
    if (!candidateEmail) { setStatus('locked'); return; }
    try {
      const res = await axios.get(`${API_URL}/api/newsletter/verify`, {
        params: { email: candidateEmail },
      });
      if (res.data?.subscribed) {
        setStatus('unlocked');
      } else {
        try { localStorage.removeItem(STORAGE_KEY); } catch {}
        setStatus('locked');
      }
    } catch {
      setStatus('locked');
    }
  }, []);

  useEffect(() => {
    let stored = '';
    try { stored = localStorage.getItem(STORAGE_KEY) || ''; } catch {}
    verify(stored);

    const handler = (e) => {
      const detail = e?.detail?.email;
      if (detail) verify(detail);
    };
    window.addEventListener('subscriber-changed', handler);
    return () => window.removeEventListener('subscriber-changed', handler);
  }, [verify]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!value || !value.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    setSubmitting(true);
    try {
      // Subscribe (idempotent on the backend — returns "already subscribed" if known)
      let refCode = '';
      try {
        const params = new URLSearchParams(window.location.search);
        refCode = params.get('ref') || localStorage.getItem('referral_code') || '';
        if (params.get('ref')) localStorage.setItem('referral_code', params.get('ref'));
      } catch {}
      await axios.post(`${API_URL}/api/newsletter/subscribe`, { email: value, source: 'learn_gate', ref_code: refCode || undefined });
      try { localStorage.setItem(STORAGE_KEY, value); } catch {}
      window.dispatchEvent(new CustomEvent('subscriber-changed', { detail: { email: value } }));
      toast.success('Welcome — Learn section unlocked!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subscription failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="gate-checking">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (status === 'unlocked') {
    return children;
  }

  // locked
  return (
    <div className="min-h-screen pt-32 pb-24 px-6" data-testid="gate-locked">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-50 rounded-full mb-6">
            <Lock className="text-indigo-600" size={28} />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-sm text-gray-700 mb-6">
            <Sparkles size={16} />
            <span>Free for subscribers</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-light mb-6 tracking-tight leading-tight">
            Unlock Learn
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-2">
            Subscribe with your email to access bite-sized lessons on
            Generative AI, Agentic AI, RAG, LLMs, and Machine Learning —
            written for high schoolers in plain English.
          </p>
          <p className="text-sm text-gray-500">
            One email a day with that day's new lessons. Unsubscribe anytime.
          </p>
        </div>

        <form onSubmit={handleUnlock} className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm" data-testid="gate-form">
          <label htmlFor="gate-email" className="block text-sm font-medium mb-2">
            Your email address
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              id="gate-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              disabled={submitting}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              data-testid="gate-email-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
              data-testid="gate-submit-btn"
            >
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Unlocking…</> : <><BookOpen size={16} /> Unlock</>}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Already a subscriber? Just enter your email — we'll recognize you.
          </p>
        </form>
      </div>
    </div>
  );
};

export default SubscriberGate;
